import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Award, Bookmark, Brain, FileText, Filter, Mail, MapPin, Save, ShieldCheck, Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TIER_STYLE = {
  Bronze:   "border-amber-700/60   text-amber-600",
  Silver:   "border-zinc-300/60    text-zinc-200",
  Gold:     "border-[#F4B400]      text-[#F4B400]",
  Diamond:  "border-[#06b6d4]      text-[#06b6d4]",
  Platinum: "border-[#8b5cf6]      text-[#8b5cf6]",
};

const TIER_RANK = { Platinum: 5, Diamond: 4, Gold: 3, Silver: 2, Bronze: 1 };

export default function RecruiterPremiumMembers() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  // ---- Filter state ----
  const [minScore, setMinScore]         = useState(0);   // 0-100
  const [minBadgeTier, setMinBadgeTier] = useState(0);   // 0=any, 3=Gold, 4=Diamond, 5=Platinum
  const [minIQ, setMinIQ]               = useState(0);   // 0=any, 100, 115, 130
  const [activeOnly, setActiveOnly]     = useState(false);
  const [selectedLangs, setSelectedLangs] = useState([]); // ["English", "Arabic", ...]

  // ---- Saved searches ----
  const [savedSearches, setSavedSearches] = useState([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newSavedName, setNewSavedName]   = useState("");
  const [savingSearch, setSavingSearch]   = useState(false);
  // Per-search "new this week" counts from the digest preview endpoint
  const [digestPreview, setDigestPreview] = useState({});

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?next=/app/premium-members"); return; }
    const ok = user.role === "recruiter" || user.role === "agency" || user.employer_pro_override === true;
    if (!ok) { navigate("/hire"); return; }
    api.get("/recruiter/premium-members")
      .then((r) => setList(r.data || []))
      .catch(() => toast.error("Could not load Premium Members."))
      .finally(() => setLoading(false));
    api.get("/recruiter/saved-searches")
      .then((r) => setSavedSearches(r.data || []))
      .catch(() => {});
    // Fetch "new this week" counts for the saved-searches strip badges
    api.post("/internal/saved-searches/preview-digest")
      .then((r) => {
        const map = {};
        (r.data || []).forEach((p) => { map[p.id] = p.new_this_week; });
        setDigestPreview(map);
      })
      .catch(() => {});
  }, [user, authLoading, navigate]);

  const openCandidate = async (id) => {
    try {
      const r = await api.get(`/recruiter/candidates/${id}`);
      setActive(r.data);
    } catch {
      toast.error("Could not open candidate profile.");
    }
  };

  const bestTier = (badges) => {
    return (badges || []).reduce((acc, b) => (TIER_RANK[b.tier] || 0) > (TIER_RANK[acc?.tier] || 0) ? b : acc, null);
  };

  // Aggregate all unique languages across the list — drives the language
  // toggle chips so the recruiter only sees languages someone actually speaks.
  const allLanguages = useMemo(() => {
    const set = new Set();
    list.forEach((c) => (c.languages || []).forEach((l) => l && set.add(l)));
    return [...set].sort();
  }, [list]);

  // Apply all client-side filters in one pass.
  const filtered = useMemo(() => {
    return list.filter((c) => {
      if ((c.elite_score || 0) < minScore) return false;
      if (minBadgeTier > 0) {
        const topRank = Math.max(0, ...(c.badges || []).map((b) => TIER_RANK[b.tier] || 0));
        if (topRank < minBadgeTier) return false;
      }
      if (minIQ > 0 && (c.iq_score || 0) < minIQ) return false;
      if (activeOnly && !c.looking_for_work) return false;
      if (selectedLangs.length > 0) {
        const has = (c.languages || []).map((l) => (l || "").toLowerCase());
        const need = selectedLangs.map((l) => l.toLowerCase());
        // Match if candidate speaks ANY of the selected languages (OR logic)
        if (!need.some((n) => has.includes(n))) return false;
      }
      return true;
    });
  }, [list, minScore, minBadgeTier, minIQ, activeOnly, selectedLangs]);

  const toggleLang = (lang) => {
    setSelectedLangs((s) => s.includes(lang) ? s.filter((x) => x !== lang) : [...s, lang]);
  };

  const clearFilters = () => {
    setMinScore(0); setMinBadgeTier(0); setMinIQ(0);
    setActiveOnly(false); setSelectedLangs([]);
  };

  const anyFilterActive = minScore > 0 || minBadgeTier > 0 || minIQ > 0 || activeOnly || selectedLangs.length > 0;

  // Apply a saved search blob back into the live filter state.
  const applySavedSearch = (s) => {
    const f = s.filters || {};
    setMinScore(Number(f.min_score) || 0);
    setMinBadgeTier(Number(f.min_badge_tier) || 0);
    setMinIQ(Number(f.min_iq) || 0);
    setActiveOnly(Boolean(f.active_only));
    setSelectedLangs(Array.isArray(f.selected_langs) ? f.selected_langs : []);
    toast.success(`Applied: ${s.name}`);
  };

  const saveCurrentSearch = async () => {
    const name = newSavedName.trim();
    if (!name) { toast.error("Name your search."); return; }
    setSavingSearch(true);
    try {
      const r = await api.post("/recruiter/saved-searches", {
        name,
        filters: {
          min_score:      minScore,
          min_badge_tier: minBadgeTier,
          min_iq:         minIQ,
          active_only:    activeOnly,
          selected_langs: selectedLangs,
        },
      });
      setSavedSearches((s) => [r.data, ...s]);
      setNewSavedName("");
      setSaveDialogOpen(false);
      toast.success(`Saved: ${name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save search.");
    } finally {
      setSavingSearch(false);
    }
  };

  const deleteSavedSearch = async (id) => {
    try {
      await api.delete(`/recruiter/saved-searches/${id}`);
      setSavedSearches((s) => s.filter((x) => x.id !== id));
      toast.success("Search deleted.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  return (
    <div data-testid="recruiter-premium-members">
      <main>
        <div className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF9500] mb-3 flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> / EMPLOYER ACCESS · ELITE TIER
          </div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95]">
            Premium members of <span className="italic text-[#FF9500]">Maqam.</span>
          </h1>
          <p className="text-white/70 mt-4 max-w-2xl">
            Workers earn a place here automatically — composite{" "}
            <span className="text-[#FF9500] font-bold">Elite Score</span> built from
            IQ, hard-quiz badges, languages, CV skills, years of experience, and
            quiz pass-rate. Anyone clearing the bar shows up live. Click to view CV.
          </p>
        </div>

        {/* Saved searches strip — one-click apply / × delete */}
        {!loading && list.length > 0 && savedSearches.length > 0 && (
          <div className="mb-3" data-testid="rpm-saved-strip">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/55 flex items-center gap-1.5">
                <Bookmark className="w-3 h-3" /> Saved searches
              </span>
            {savedSearches.map((s) => {
              const newCount = digestPreview[s.id] || 0;
              return (
                <span key={s.id} className="inline-flex items-center gap-1 bg-[#141416] border border-white/15 hover:border-[#FF9500]/60 transition-colors">
                  <button
                    type="button"
                    onClick={() => applySavedSearch(s)}
                    className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white/85 hover:text-[#FF9500] flex items-center gap-2"
                    data-testid={`rpm-saved-apply-${s.id}`}
                  >
                    {s.name}
                    {newCount > 0 && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#FF9500] text-black font-bold text-[9px]"
                        title="New candidates this week (you'll be emailed)"
                        data-testid={`rpm-saved-new-${s.id}`}
                      >
                        <Mail className="w-2.5 h-2.5" />
                        +{newCount}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteSavedSearch(s.id)}
                    className="px-2 py-1.5 text-white/40 hover:text-white"
                    aria-label={`Delete saved search ${s.name}`}
                    data-testid={`rpm-saved-delete-${s.id}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
            </div>
            <div className="mt-1.5 font-mono text-[9px] uppercase tracking-widest text-white/40 flex items-center gap-1.5">
              <Mail className="w-2.5 h-2.5" />
              You'll get a weekly digest email when new candidates match a saved search.
            </div>
          </div>
        )}

        {/* Filter bar — gives recruiters Strava-tier narrowing in 1 second */}
        {!loading && list.length > 0 && (
          <div className="bg-[#141416] border border-white/10 p-5 mb-6" data-testid="rpm-filter-bar">
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/55 flex items-center gap-2">
                <Filter className="w-3 h-3" /> Filter elite pool
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/45" data-testid="rpm-filter-count">
                  Showing <span className="text-[#FF9500] font-bold">{filtered.length}</span> / {list.length}
                </span>
                {anyFilterActive && (
                  <>
                    <button
                      type="button"
                      onClick={() => setSaveDialogOpen(true)}
                      className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-[#FF9500] hover:text-black hover:bg-[#FF9500] px-2 py-1 border border-[#FF9500]/60 transition-colors"
                      data-testid="rpm-save-search-open"
                    >
                      <Save className="w-3 h-3" /> Save
                    </button>
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="font-mono text-[10px] uppercase tracking-widest text-white/55 hover:text-white px-2 py-1 border border-white/15 hover:border-white"
                      data-testid="rpm-filter-clear"
                    >
                      Reset
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {/* Elite Score slider */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="font-mono text-[10px] uppercase tracking-widest text-white/65">Min Elite Score</label>
                  <span className="font-mono text-xs text-[#FF9500] font-bold">{minScore}</span>
                </div>
                <input
                  type="range" min={0} max={100} step={5}
                  value={minScore}
                  onChange={(e) => setMinScore(Number(e.target.value))}
                  className="w-full accent-[#FF9500]"
                  data-testid="rpm-filter-min-score"
                />
              </div>

              {/* Min badge tier */}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-white/65 mb-1 block">Min badge tier</label>
                <select
                  value={minBadgeTier}
                  onChange={(e) => setMinBadgeTier(Number(e.target.value))}
                  className="w-full bg-transparent text-white border-b border-white/15 focus:border-[#FF9500] outline-none py-2 text-sm"
                  data-testid="rpm-filter-tier"
                >
                  <option value={0} className="bg-[#141416]">Any</option>
                  <option value={3} className="bg-[#141416]">Gold +</option>
                  <option value={4} className="bg-[#141416]">Diamond +</option>
                  <option value={5} className="bg-[#141416]">Platinum only</option>
                </select>
              </div>

              {/* Min IQ */}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-white/65 mb-1 block">Min IQ</label>
                <select
                  value={minIQ}
                  onChange={(e) => setMinIQ(Number(e.target.value))}
                  className="w-full bg-transparent text-white border-b border-white/15 focus:border-[#FF9500] outline-none py-2 text-sm"
                  data-testid="rpm-filter-iq"
                >
                  <option value={0} className="bg-[#141416]">Any</option>
                  <option value={100} className="bg-[#141416]">IQ ≥ 100</option>
                  <option value={115} className="bg-[#141416]">IQ ≥ 115</option>
                  <option value={130} className="bg-[#141416]">IQ ≥ 130 (genius)</option>
                </select>
              </div>

              {/* Active only */}
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={activeOnly}
                    onChange={(e) => setActiveOnly(e.target.checked)}
                    className="w-4 h-4 accent-[#FF9500]"
                    data-testid="rpm-filter-active"
                  />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-white/80">
                    🔥 Actively looking only
                  </span>
                </label>
              </div>
            </div>

            {/* Language chips (rendered only if there's >0 known languages) */}
            {allLanguages.length > 0 && (
              <div className="mt-5 pt-5 border-t border-white/10">
                <label className="font-mono text-[10px] uppercase tracking-widest text-white/65 mb-2 block">
                  Speaks any of
                </label>
                <div className="flex flex-wrap gap-2" data-testid="rpm-filter-langs">
                  {allLanguages.map((lang) => {
                    const on = selectedLangs.includes(lang);
                    return (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => toggleLang(lang)}
                        className={`px-3 py-1 font-mono text-[10px] uppercase tracking-widest border transition-colors ${
                          on
                            ? "bg-[#FF9500] text-black border-[#FF9500]"
                            : "border-white/20 text-white/70 hover:border-white/60 hover:text-white"
                        }`}
                        data-testid={`rpm-filter-lang-${lang.toLowerCase()}`}
                      >
                        {lang}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Inline save-search dialog */}
            {saveDialogOpen && (
              <div className="mt-5 pt-5 border-t border-[#FF9500]/30" data-testid="rpm-save-dialog">
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#FF9500] mb-2 block">
                  Name this search
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    autoFocus
                    value={newSavedName}
                    onChange={(e) => setNewSavedName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveCurrentSearch(); if (e.key === "Escape") setSaveDialogOpen(false); }}
                    maxLength={80}
                    placeholder="e.g. Diamond Python devs · IQ 130+ · Arabic"
                    className="flex-1 bg-black/40 border border-white/15 focus:border-[#FF9500] outline-none px-3 py-2 text-sm text-white placeholder:text-white/30"
                    data-testid="rpm-save-name-input"
                  />
                  <button
                    type="button"
                    onClick={saveCurrentSearch}
                    disabled={savingSearch || !newSavedName.trim()}
                    className="inline-flex items-center gap-1.5 bg-[#FF9500] text-black hover:bg-[#FF9500]/85 px-4 py-2 font-bold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="rpm-save-confirm"
                  >
                    <Save className="w-3.5 h-3.5" />
                    {savingSearch ? "Saving…" : "Save search"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSaveDialogOpen(false); setNewSavedName(""); }}
                    className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-white/55 hover:text-white border border-white/15 hover:border-white"
                    data-testid="rpm-save-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="border border-dashed border-white/15 p-16 text-center text-white/55">Loading Premium members…</div>
        ) : list.length === 0 ? (
          <div className="border border-dashed border-white/15 p-16 text-center text-white/55">
            No Premium members yet. As workers complete hard quizzes + the IQ test, they'll show up here automatically.
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-white/15 p-16 text-center text-white/55" data-testid="rpm-no-results">
            No one matches your filters. <button onClick={clearFilters} className="text-[#FF9500] underline">Reset filters</button> to see all {list.length} candidates.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rpm-list">
            {filtered.map((c) => {
              const top = bestTier(c.badges);
              return (
                <article
                  key={c.candidate_id}
                  onClick={() => openCandidate(c.candidate_id)}
                  className="bg-[#141416] border border-white/10 hover:border-[#FF9500]/60 p-5 cursor-pointer transition-colors"
                  data-testid={`rpm-card-${c.candidate_id}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-12 h-12 bg-[#FF9500]/15 text-[#FF9500] border border-[#FF9500]/30 flex items-center justify-center font-display font-bold text-lg flex-shrink-0">
                      {c.display_name.split(" ").map((x) => x[0]).join("").slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-lg leading-tight truncate">{c.display_name}</div>
                      <div className="text-sm text-white/65 truncate">
                        {c.job_title || c.headline || (c.experience_years > 0 ? `${c.experience_years} yrs experience` : "Maqam member")}
                      </div>
                      <div className="text-xs text-white/45 flex items-center gap-1.5 mt-1">
                        <MapPin className="w-3 h-3" />
                        {(c.preferred_cities || []).join(", ") || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {top && (
                      <div className={`inline-flex items-center gap-1.5 px-2 py-1 border ${TIER_STYLE[top.tier]} bg-black/30`}>
                        <Award className="w-3 h-3" />
                        <span className="font-mono text-[10px] font-bold">{top.tier}</span>
                        <span className="text-[10px] text-white/80 truncate max-w-[100px]">{top.skill}</span>
                      </div>
                    )}
                    {c.iq_score && (
                      <div className="inline-flex items-center gap-1.5 px-2 py-1 border border-[#8b5cf6]/50 text-[#8b5cf6] bg-black/30" title={`IQ ${c.iq_score} · ${c.iq_percentile}th percentile`}>
                        <Brain className="w-3 h-3" />
                        <span className="font-mono text-[10px] font-bold">IQ {c.iq_score}</span>
                      </div>
                    )}
                    {c.looking_for_work && (
                      <span className="px-2 py-1 border border-[#FF9500]/40 text-[#FF9500] font-mono text-[9px] uppercase tracking-widest">🔥 Active</span>
                    )}
                  </div>

                  {c.badges?.length > 1 && (
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/45 mb-3">
                      {c.badges.length} verified skills · all hard-tier
                    </div>
                  )}

                  {/* Elite-score breakdown chips — let the recruiter see
                      WHY this candidate qualifies at a glance. */}
                  <div className="grid grid-cols-3 gap-1.5 mb-3" data-testid={`rpm-breakdown-${c.candidate_id}`}>
                    {[
                      { k: "iq",         label: "IQ",      max: 25 },
                      { k: "badges",     label: "Badges",  max: 30 },
                      { k: "languages",  label: "Langs",   max: 12 },
                      { k: "skills",     label: "Skills",  max: 10 },
                      { k: "experience", label: "Exp",     max: 8 },
                      { k: "quizzes",    label: "Quizzes", max: 15 },
                    ].map((row) => {
                      const v = (c.elite_breakdown || {})[row.k] || 0;
                      const pct = Math.round((v / row.max) * 100);
                      return (
                        <div key={row.k} className="bg-black/30 px-2 py-1">
                          <div className="font-mono text-[8px] uppercase tracking-widest text-white/45">{row.label}</div>
                          <div className="flex items-center gap-1">
                            <div className="flex-1 h-1 bg-white/10">
                              <div className="h-full bg-[#FF9500]" style={{ width: `${Math.min(100, pct)}%` }} />
                            </div>
                            <span className="font-mono text-[10px] text-white/80">{v}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="pt-3 border-t border-white/10 flex items-center justify-between">
                    <span className="text-xs text-white/55 flex items-center gap-1.5">
                      <FileText className="w-3 h-3" /> Click to view CV
                    </span>
                    <div className="text-right">
                      <div className="font-mono text-[9px] uppercase tracking-widest text-white/45">Elite score</div>
                      <span className="font-display font-bold text-2xl text-[#FF9500]" data-testid={`rpm-score-${c.candidate_id}`}>
                        {c.elite_score}
                      </span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>

      {/* Candidate detail modal */}
      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4" data-testid="rpm-detail-modal">
          <div className="bg-[#141416] text-white w-full max-w-3xl border border-white/15 max-h-[92vh] flex flex-col">
            <div className="flex items-start justify-between p-6 border-b border-white/10">
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-white/50 mb-1">Candidate · Premium tier</div>
                <h3 className="font-display font-black text-3xl tracking-tighter">{active.display_name}</h3>
                <div className="text-white/70 text-sm mt-1">{active.headline}</div>
                <div className="text-xs text-white/50 mt-1">{active.experience_years} years experience · {(active.preferred_cities || []).join(", ")}</div>
              </div>
              <button onClick={() => setActive(null)} className="p-1 hover:bg-white/10"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {active.badges?.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">Verified skills</div>
                  <div className="flex flex-wrap gap-2">
                    {active.badges.map((b) => (
                      <div key={`${b.skill}-${b.tier}`} className={`inline-flex items-center gap-2 px-3 py-2 border ${TIER_STYLE[b.tier]} bg-black/30`}>
                        <Award className={`w-4 h-4 ${TIER_STYLE[b.tier]?.split(" ")[2] || ""}`} />
                        <div>
                          <div className="font-bold text-sm">{b.tier} · {b.skill}</div>
                          <div className="text-[10px] font-mono text-white/55">
                            {b.score}% · {b.difficulty?.toUpperCase()} · Integrity {b.integrity_score}/100
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {active.skills?.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">All skills</div>
                  <div className="flex flex-wrap gap-1">
                    {active.skills.map((s) => (
                      <span key={s} className="px-2 py-1 bg-white/[0.04] text-xs border border-white/10 text-white/80">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {active.cv_text_preview && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2 flex items-center gap-2">
                    <FileText className="w-3 h-3" /> Full CV
                  </div>
                  <pre className="whitespace-pre-wrap text-sm text-white/85 bg-black/40 p-4 border border-white/10 max-h-[50vh] overflow-y-auto leading-relaxed">
                    {active.cv_text_preview}
                  </pre>
                </div>
              )}
              {!active.cv_text_preview && (
                <div className="border border-dashed border-white/15 p-6 text-center text-white/50 text-sm">
                  <ShieldCheck className="w-5 h-5 mx-auto mb-2 text-white/40" />
                  This candidate hasn't uploaded a CV yet. Send an intro request to ask for one.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
